// i18n bootstrap. English is the source/fallback. Picks language from localStorage
// or browser navigator on first load.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en";
import fr from "./fr";
import hi from "./hi";
import ar from "./ar";
import it from "./it";
import es from "./es";
import zh from "./zh";
import ja from "./ja";
import ko from "./ko";
import sw from "./sw";
import ur from "./ur";

// Order shown in the language dropdown. English is first as the source language,
// then most-asked languages including RTL ones (ar, ur) prioritised at the top
// per parent feedback.
export const SUPPORTED = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "ur", label: "اردو", dir: "rtl" },
  { code: "hi", label: "हिन्दी", dir: "ltr" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "es", label: "Español", dir: "ltr" },
  { code: "it", label: "Italiano", dir: "ltr" },
  { code: "zh", label: "中文", dir: "ltr" },
  { code: "ja", label: "日本語", dir: "ltr" },
  { code: "ko", label: "한국어", dir: "ltr" },
  { code: "sw", label: "Kiswahili", dir: "ltr" },
];

function detectInitial() {
  try {
    const saved = localStorage.getItem("we_lang");
    if (saved && SUPPORTED.find((s) => s.code === saved)) return saved;
  } catch {
    // ignore
  }
  // First-visit rule (Feb 2026): DEFAULT TO ENGLISH regardless of
  // browser locale. Analytics show most international users start in
  // English (school/work language) and can switch via the language
  // menu if they prefer their native language. This avoids the
  // "why is my kid's app suddenly in Chinese?" surprise on shared
  // devices/schools where navigator.language is set by a parent.
  return "en";
}

// Mark whether this load is the first time we've auto-detected the
// browser's preferred language (vs. honouring a manual `we_lang` choice).
// Used below to persist the detection and welcome non-English visitors.
function isFirstAutoDetect() {
  try {
    return !localStorage.getItem("we_lang");
  } catch {
    return false;
  }
}

const initial = detectInitial();
const wasAutoDetected = isFirstAutoDetect();

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
    hi: { translation: hi },
    ar: { translation: ar },
    ur: { translation: ur },
    it: { translation: it },
    es: { translation: es },
    zh: { translation: zh },
    ja: { translation: ja },
    ko: { translation: ko },
    sw: { translation: sw },
  },
  lng: initial,
  fallbackLng: "en",
  interpolation: { escapeValue: false },
  returnNull: false,
});

// Apply the document direction + lang attribute on init and language change.
function applyDirection(code) {
  const dir = SUPPORTED.find((s) => s.code === code)?.dir || "ltr";
  if (typeof document !== "undefined") {
    document.documentElement.lang = code;
    document.documentElement.dir = dir;
    document.body.classList.toggle("rtl", dir === "rtl");
  }
}
applyDirection(initial);
i18n.on("languageChanged", (lng) => {
  try { localStorage.setItem("we_lang", lng); } catch { /* ignore */ }
  applyDirection(lng);
});

// First-time auto-detect: persist the picked language so the language
// switcher reflects it across reloads, and surface a one-time toast for
// non-English visitors letting them know they can switch back.
if (wasAutoDetected) {
  try { localStorage.setItem("we_lang", initial); } catch { /* ignore */ }
  if (initial !== "en" && typeof window !== "undefined") {
    const label = SUPPORTED.find((s) => s.code === initial)?.label || initial;
    // Defer so the toast container in <App> has mounted.
    window.setTimeout(() => {
      import("sonner").then(({ toast }) => {
        toast(`👋 ${label} · ${initial.toUpperCase()}`, {
          description: "We picked your language automatically. Tap the 🌐 menu to change.",
          duration: 6000,
        });
      }).catch(() => { /* sonner missing — silent */ });
    }, 1500);
  }
}

export default i18n;
